import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Settings, Loader2, CheckCircle2, Shield, Linkedin, Facebook,
  Instagram, Save, Plus, X, Users, Calendar, Clock, Sparkles,
  Repeat, Timer, Zap, Link2, Unlink, ExternalLink, AlertCircle
} from "lucide-react";

const PLATFORM_CONFIG = {
  linkedin: { label: "LinkedIn", icon: Linkedin, color: "#0a66c2" },
  facebook: { label: "Facebook", icon: Facebook, color: "#1877f2" },
  instagram: { label: "Instagram", icon: Instagram, color: "#e1306c" },
};

const TONES = [
  { value: "professionnel", label: "Professionnel" },
  { value: "decontracte", label: "Decontracte" },
  { value: "inspirant", label: "Inspirant" },
  { value: "educatif", label: "Educatif" },
];

const DAYS = [
  { value: "lundi", label: "Lun" },
  { value: "mardi", label: "Mar" },
  { value: "mercredi", label: "Mer" },
  { value: "jeudi", label: "Jeu" },
  { value: "vendredi", label: "Ven" },
  { value: "samedi", label: "Sam" },
  { value: "dimanche", label: "Dim" },
];

const DELAY_OPTIONS = [
  { value: 0, label: "Desactive (validation manuelle uniquement)" },
  { value: 12, label: "12 heures" },
  { value: 24, label: "24 heures" },
  { value: 48, label: "48 heures" },
  { value: 72, label: "72 heures" },
  { value: 168, label: "7 jours" },
];

function Toggle({ value, onChange, testId }) {
  return (
    <button
      onClick={() => onChange(!value)}
      className={`w-10 h-5 rounded-full transition-colors relative flex-shrink-0 ${value ? "bg-[#10b981]" : "bg-slate-600"}`}
      data-testid={testId}
    >
      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${value ? "left-[22px]" : "left-0.5"}`} />
    </button>
  );
}

export function MarketingSettings({ api }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [newValidator, setNewValidator] = useState("");
  const [metaAccounts, setMetaAccounts] = useState(null);
  const [metaSettings, setMetaSettings] = useState({ app_id: "", app_secret: "", redirect_uri: "" });
  const [metaLoading, setMetaLoading] = useState(false);
  const [showMetaConfig, setShowMetaConfig] = useState(false);

  useEffect(() => { loadSettings(); loadMetaAccounts(); }, []);

  const loadMetaAccounts = async () => {
    try {
      const r = await api.get("/marketing/meta/accounts");
      setMetaAccounts(r.data);
    } catch { setMetaAccounts({ configured: false, pages: [], instagram_accounts: [] }); }
  };

  const saveMetaSettings = async () => {
    setMetaLoading(true);
    try {
      await api.put("/marketing/meta/settings", metaSettings);
      setShowMetaConfig(false);
      loadMetaAccounts();
    } catch (e) { alert(e.response?.data?.detail || "Erreur"); }
    setMetaLoading(false);
  };

  const connectMeta = async () => {
    setMetaLoading(true);
    try {
      const r = await api.get("/marketing/meta/auth-url");
      window.open(r.data.auth_url, "_blank");
    } catch (e) { alert(e.response?.data?.detail || "Configurez d'abord votre App Meta"); }
    setMetaLoading(false);
  };

  const disconnectMeta = async () => {
    if (!window.confirm("Deconnecter tous les comptes Meta ?")) return;
    try {
      await api.delete("/marketing/meta/disconnect");
      loadMetaAccounts();
    } catch (e) { alert("Erreur"); }
  };

  const loadSettings = async () => {
    setLoading(true);
    try {
      const resp = await api.get("/marketing/settings");
      setSettings(resp.data);
    } catch {
      setSettings({
        validation_required: false, validators: [], auto_approve_delay_hours: 0,
        platforms_enabled: ["linkedin", "facebook", "instagram"], default_tone: "professionnel",
        auto_schedule: false,
        auto_generate: { enabled: false, frequency_per_week: 3, preferred_topics: [], auto_create_prompt: "" },
        weekly_slots: [],
        preferred_hours: { linkedin: "09:00", facebook: "12:00", instagram: "18:00" },
      });
    }
    setLoading(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/marketing/settings", settings);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) { alert(e.response?.data?.detail || "Erreur"); }
    setSaving(false);
  };

  const togglePlatform = (p) => {
    setSettings(s => ({ ...s, platforms_enabled: s.platforms_enabled.includes(p) ? s.platforms_enabled.filter(x => x !== p) : [...s.platforms_enabled, p] }));
  };

  const addValidator = () => {
    if (!newValidator.trim()) return;
    setSettings(s => ({ ...s, validators: [...(s.validators || []), newValidator.trim()] }));
    setNewValidator("");
  };

  const removeValidator = (v) => {
    setSettings(s => ({ ...s, validators: (s.validators || []).filter(x => x !== v) }));
  };

  // Weekly slots
  const addSlot = () => {
    setSettings(s => ({
      ...s,
      weekly_slots: [...(s.weekly_slots || []), { day: "lundi", time: "09:00", platform: "linkedin" }],
    }));
  };

  const updateSlot = (idx, field, value) => {
    setSettings(s => ({
      ...s,
      weekly_slots: (s.weekly_slots || []).map((slot, i) => i === idx ? { ...slot, [field]: value } : slot),
    }));
  };

  const removeSlot = (idx) => {
    setSettings(s => ({ ...s, weekly_slots: (s.weekly_slots || []).filter((_, i) => i !== idx) }));
  };

  // Auto generate
  const updateAutoGen = (field, value) => {
    setSettings(s => ({ ...s, auto_generate: { ...(s.auto_generate || {}), [field]: value } }));
  };

  // Preferred hours
  const updatePrefHour = (platform, value) => {
    setSettings(s => ({ ...s, preferred_hours: { ...(s.preferred_hours || {}), [platform]: value } }));
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#10b981]" /></div>;
  if (!settings) return null;

  const autoGen = settings.auto_generate || {};

  return (
    <div data-testid="settings-tab" className="max-w-2xl space-y-5">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-8 h-8 rounded-lg bg-[#10b981]/10 flex items-center justify-center">
          <Settings className="w-4 h-4 text-[#10b981]" />
        </div>
        <div>
          <h3 className="text-sm font-bold">Parametres Reseaux Sociaux</h3>
          <p className="text-[10px] text-slate-500">Validation, automatisation, plateformes et planification</p>
        </div>
      </div>

      {/* ── 1. Plateformes activees ── */}
      <div className="bg-[#14101c] border border-white/5 rounded-xl p-4">
        <h4 className="text-xs font-bold mb-3">Plateformes activees</h4>
        <div className="flex flex-wrap gap-2">
          {Object.entries(PLATFORM_CONFIG).map(([k, cfg]) => {
            const Icon = cfg.icon;
            const active = settings.platforms_enabled.includes(k);
            return (
              <button key={k} onClick={() => togglePlatform(k)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-all ${active ? "text-white" : "bg-[#0b0811] text-slate-500 border border-white/5"}`}
                style={active ? { background: cfg.color } : {}} data-testid={`settings-platform-${k}`}>
                <Icon className="w-4 h-4" /> {cfg.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── 2. Workflow de validation ── */}
      <div className="bg-[#14101c] border border-white/5 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <Shield className="w-4 h-4 text-amber-400" />
          <h4 className="text-xs font-bold">Workflow de validation</h4>
        </div>

        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-xs text-slate-300">Validation obligatoire</p>
            <p className="text-[10px] text-slate-500">Les posts doivent etre approuves avant publication</p>
          </div>
          <Toggle value={settings.validation_required} onChange={v => setSettings(s => ({ ...s, validation_required: v }))} testId="toggle-validation" />
        </div>

        {settings.validation_required && (
          <div className="space-y-3 mt-3 pt-3 border-t border-white/5">
            {/* Validators */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-3.5 h-3.5 text-slate-400" />
                <span className="text-[10px] text-slate-500 uppercase">Validateurs</span>
              </div>
              <div className="flex flex-wrap gap-1 mb-2">
                {(settings.validators || []).map((v, i) => (
                  <span key={i} className="text-[10px] px-2 py-1 rounded-full bg-amber-500/10 text-amber-400 flex items-center gap-1">
                    {v} <button onClick={() => removeValidator(v)}><X className="w-2.5 h-2.5" /></button>
                  </span>
                ))}
              </div>
              <div className="flex gap-1">
                <Input placeholder="Email du validateur" value={newValidator} onChange={e => setNewValidator(e.target.value)}
                  className="bg-[#0b0811] border-white/10 text-white text-xs flex-1"
                  onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addValidator())} data-testid="validator-input" />
                <Button onClick={addValidator} size="sm" variant="outline" className="border-white/10 text-slate-400 text-xs"><Plus className="w-3 h-3" /></Button>
              </div>
            </div>

            {/* Auto-approve delay */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Timer className="w-3.5 h-3.5 text-slate-400" />
                <span className="text-[10px] text-slate-500 uppercase">Auto-approbation si pas de reponse</span>
              </div>
              <select value={settings.auto_approve_delay_hours || 0}
                onChange={e => setSettings(s => ({ ...s, auto_approve_delay_hours: parseInt(e.target.value) }))}
                className="w-full bg-[#0b0811] border border-white/10 text-white rounded-lg px-3 py-2 text-xs"
                data-testid="auto-approve-delay">
                {DELAY_OPTIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
              <p className="text-[9px] text-slate-600 mt-1">Si le validateur ne repond pas dans ce delai, le post est automatiquement approuve</p>
            </div>
          </div>
        )}
      </div>

      {/* ── 3. Generation automatique IA ── */}
      <div className="bg-[#14101c] border border-white/5 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-4 h-4 text-[#10b981]" />
          <h4 className="text-xs font-bold">Generation automatique IA</h4>
        </div>

        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-xs text-slate-300">Activer la generation auto</p>
            <p className="text-[10px] text-slate-500">L'IA genere automatiquement des posts selon vos preferences</p>
          </div>
          <Toggle value={autoGen.enabled} onChange={v => updateAutoGen("enabled", v)} testId="toggle-auto-generate" />
        </div>

        {autoGen.enabled && (
          <div className="space-y-3 mt-3 pt-3 border-t border-white/5">
            <div>
              <label className="text-[10px] text-slate-500 uppercase mb-1 block">Frequence (posts / semaine)</label>
              <div className="flex gap-2 items-center">
                <input type="range" min={1} max={14} value={autoGen.frequency_per_week || 3}
                  onChange={e => updateAutoGen("frequency_per_week", parseInt(e.target.value))}
                  className="flex-1 accent-[#10b981]" data-testid="auto-gen-frequency" />
                <span className="text-sm font-bold text-[#10b981] w-8 text-center">{autoGen.frequency_per_week || 3}</span>
              </div>
            </div>

            <div>
              <label className="text-[10px] text-slate-500 uppercase mb-1 block">Prompt de generation automatique</label>
              <textarea value={autoGen.auto_create_prompt || ""}
                onChange={e => updateAutoGen("auto_create_prompt", e.target.value)}
                placeholder="Ex: Genere des posts sur nos services de conseil fiscal, mettant en avant notre expertise et nos reussites clients..."
                className="w-full bg-[#0b0811] border border-white/10 text-white rounded-lg px-3 py-2 text-xs h-20 resize-none"
                data-testid="auto-gen-prompt" />
            </div>

            <div>
              <label className="text-[10px] text-slate-500 uppercase mb-1 block">Themes preferes (optionnel)</label>
              <Input value={(autoGen.preferred_topics || []).join(", ")}
                onChange={e => updateAutoGen("preferred_topics", e.target.value.split(",").map(t => t.trim()).filter(Boolean))}
                placeholder="Ex: fiscalite, startups, innovation, accompagnement"
                className="bg-[#0b0811] border-white/10 text-white text-xs" />
              <p className="text-[9px] text-slate-600 mt-1">Separez par des virgules</p>
            </div>

            <div>
              <label className="text-[10px] text-slate-500 uppercase mb-1 block">Ton par defaut</label>
              <div className="flex flex-wrap gap-2">
                {TONES.map(t => (
                  <button key={t.value} onClick={() => setSettings(s => ({ ...s, default_tone: t.value }))}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      settings.default_tone === t.value ? "bg-[#10b981]/15 text-[#10b981] border border-[#10b981]/30" : "bg-[#0b0811] text-slate-500 border border-white/5"
                    }`}>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── 4. Creneaux hebdomadaires ── */}
      <div className="bg-[#14101c] border border-white/5 rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-blue-400" />
            <h4 className="text-xs font-bold">Creneaux de publication hebdomadaires</h4>
          </div>
          <Button onClick={addSlot} size="sm" variant="outline" className="border-white/10 text-slate-400 text-xs" data-testid="add-slot-btn">
            <Plus className="w-3 h-3 mr-1" /> Creneau
          </Button>
        </div>
        <p className="text-[10px] text-slate-500 mb-3">Definissez les jours et heures de publication pour chaque plateforme</p>

        {(settings.weekly_slots || []).length === 0 ? (
          <div className="text-center py-4 text-slate-600 text-[10px]">Aucun creneau defini. Ajoutez-en pour planifier automatiquement.</div>
        ) : (
          <div className="space-y-2">
            {(settings.weekly_slots || []).map((slot, idx) => {
              const pCfg = PLATFORM_CONFIG[slot.platform] || PLATFORM_CONFIG.linkedin;
              const PIcon = pCfg.icon;
              return (
                <div key={idx} className="flex items-center gap-2 bg-[#0b0811] border border-white/5 rounded-lg px-3 py-2" data-testid={`slot-${idx}`}>
                  <select value={slot.day} onChange={e => updateSlot(idx, "day", e.target.value)}
                    className="bg-transparent border border-white/10 text-white rounded px-2 py-1 text-xs">
                    {DAYS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                  </select>
                  <Input type="time" value={slot.time} onChange={e => updateSlot(idx, "time", e.target.value)}
                    className="bg-transparent border-white/10 text-white text-xs w-24" />
                  <div className="flex gap-1 flex-1">
                    {Object.entries(PLATFORM_CONFIG).map(([k, cfg]) => {
                      const Icon = cfg.icon;
                      return (
                        <button key={k} onClick={() => updateSlot(idx, "platform", k)}
                          className={`p-1.5 rounded-lg transition-all ${slot.platform === k ? "text-white" : "text-slate-600 hover:text-slate-400"}`}
                          style={slot.platform === k ? { background: cfg.color } : {}}>
                          <Icon className="w-3.5 h-3.5" />
                        </button>
                      );
                    })}
                  </div>
                  <button onClick={() => removeSlot(idx)} className="p-1 hover:bg-red-500/10 rounded"><X className="w-3 h-3 text-red-400" /></button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── 5. Heures preferees par plateforme ── */}
      <div className="bg-[#14101c] border border-white/5 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <Clock className="w-4 h-4 text-purple-400" />
          <h4 className="text-xs font-bold">Heures preferees par plateforme</h4>
        </div>
        <p className="text-[10px] text-slate-500 mb-3">Heure par defaut si aucun creneau specifique n'est defini</p>
        <div className="space-y-2">
          {Object.entries(PLATFORM_CONFIG).map(([k, cfg]) => {
            const Icon = cfg.icon;
            return (
              <div key={k} className="flex items-center gap-3">
                <div className="flex items-center gap-1.5 w-28">
                  <Icon className="w-3.5 h-3.5" style={{ color: cfg.color }} />
                  <span className="text-xs" style={{ color: cfg.color }}>{cfg.label}</span>
                </div>
                <Input type="time" value={(settings.preferred_hours || {})[k] || "09:00"}
                  onChange={e => updatePrefHour(k, e.target.value)}
                  className="bg-[#0b0811] border-white/10 text-white text-xs w-28" data-testid={`pref-hour-${k}`} />
              </div>
            );
          })}
        </div>
      </div>

      {/* ── 6. Planification automatique ── */}
      <div className="bg-[#14101c] border border-white/5 rounded-xl p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Repeat className="w-4 h-4 text-indigo-400" />
            <div>
              <h4 className="text-xs font-bold">Planification automatique</h4>
              <p className="text-[10px] text-slate-500">Les posts generes sont automatiquement places dans le calendrier</p>
            </div>
          </div>
          <Toggle value={settings.auto_schedule} onChange={v => setSettings(s => ({ ...s, auto_schedule: v }))} testId="toggle-auto-schedule" />
        </div>
      </div>

      {/* ── 7. Connexion Meta (Facebook + Instagram) ── */}
      <div className="bg-[#14101c] border border-white/5 rounded-xl p-4" data-testid="meta-connection-section">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#1877f2] to-[#e1306c] flex items-center justify-center">
            <Link2 className="w-3 h-3 text-white" />
          </div>
          <div>
            <h4 className="text-xs font-bold">Connexion Meta (Facebook + Instagram)</h4>
            <p className="text-[10px] text-slate-500">Publiez directement sur vos pages Facebook et comptes Instagram</p>
          </div>
        </div>

        {metaAccounts?.connected ? (
          <div className="space-y-2">
            {metaAccounts.pages?.map(p => (
              <div key={p.page_id} className="flex items-center gap-2 bg-[#1877f2]/10 rounded-lg px-3 py-2">
                <Facebook className="w-4 h-4 text-[#1877f2]" />
                <span className="text-xs text-white font-medium">{p.name}</span>
                <span className="text-[9px] text-slate-500 ml-auto">{p.category}</span>
              </div>
            ))}
            {metaAccounts.instagram_accounts?.map(ig => (
              <div key={ig.ig_user_id} className="flex items-center gap-2 bg-[#e1306c]/10 rounded-lg px-3 py-2">
                <Instagram className="w-4 h-4 text-[#e1306c]" />
                <span className="text-xs text-white font-medium">@{ig.username}</span>
                <span className="text-[9px] text-slate-500 ml-auto">{ig.followers_count?.toLocaleString()} abonnes</span>
              </div>
            ))}
            <div className="flex gap-2 mt-2">
              <Button onClick={connectMeta} variant="outline" size="sm" className="text-[10px] border-white/10 text-slate-400" data-testid="meta-reconnect-btn">
                <Link2 className="w-3 h-3 mr-1" /> Reconnecter
              </Button>
              <Button onClick={disconnectMeta} variant="outline" size="sm" className="text-[10px] border-red-500/20 text-red-400" data-testid="meta-disconnect-btn">
                <Unlink className="w-3 h-3 mr-1" /> Deconnecter
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {!metaAccounts?.configured && (
              <div className="flex items-start gap-2 bg-amber-500/5 border border-amber-500/10 rounded-lg px-3 py-2">
                <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-[10px] text-amber-300 font-medium">Configuration requise</p>
                  <p className="text-[9px] text-slate-500">
                    Creez une App Meta sur <a href="https://developers.facebook.com/apps/" target="_blank" rel="noopener noreferrer" className="text-[#1877f2] underline">developers.facebook.com</a> puis renseignez vos identifiants ci-dessous
                  </p>
                </div>
              </div>
            )}

            {showMetaConfig ? (
              <div className="space-y-2 bg-[#0b0811] rounded-lg p-3 border border-white/5">
                <Input value={metaSettings.app_id} onChange={e => setMetaSettings(s => ({ ...s, app_id: e.target.value }))}
                  placeholder="App ID Meta" className="bg-transparent border-white/10 text-white text-xs" data-testid="meta-app-id" />
                <Input value={metaSettings.app_secret} onChange={e => setMetaSettings(s => ({ ...s, app_secret: e.target.value }))}
                  placeholder="App Secret Meta" type="password" className="bg-transparent border-white/10 text-white text-xs" data-testid="meta-app-secret" />
                <Input value={metaSettings.redirect_uri} onChange={e => setMetaSettings(s => ({ ...s, redirect_uri: e.target.value }))}
                  placeholder="URI de redirection (ex: https://votresite.com/meta/callback)" className="bg-transparent border-white/10 text-white text-xs" data-testid="meta-redirect-uri" />
                <div className="flex gap-2">
                  <Button onClick={saveMetaSettings} disabled={metaLoading || !metaSettings.app_id} className="bg-[#1877f2] text-white text-xs font-bold" data-testid="meta-save-config">
                    {metaLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />} Sauvegarder
                  </Button>
                  <Button onClick={() => setShowMetaConfig(false)} variant="ghost" className="text-xs text-slate-500">Annuler</Button>
                </div>
              </div>
            ) : (
              <div className="flex gap-2">
                <Button onClick={() => setShowMetaConfig(true)} variant="outline" className="text-xs border-white/10 text-slate-300" data-testid="meta-configure-btn">
                  <Settings className="w-3 h-3 mr-1" /> Configurer l'App Meta
                </Button>
                {metaAccounts?.configured && (
                  <Button onClick={connectMeta} disabled={metaLoading} className="bg-gradient-to-r from-[#1877f2] to-[#e1306c] text-white text-xs font-bold" data-testid="meta-connect-btn">
                    {metaLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <ExternalLink className="w-3 h-3 mr-1" />} Connecter Facebook & Instagram
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Save */}
      <Button onClick={save} disabled={saving} className="bg-[#10b981] text-white hover:bg-[#059669] font-bold" data-testid="save-settings-btn">
        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : saved ? <CheckCircle2 className="w-4 h-4 mr-2" /> : <Save className="w-4 h-4 mr-2" />}
        {saved ? "Sauvegarde !" : "Sauvegarder les parametres"}
      </Button>
    </div>
  );
}
